import { useCallback, useEffect, useRef, useState } from "react";
import { UpdateCardButton } from "./usage-notice";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelPlan,
  changePlan,
  checkoutOutcome,
  clearCheckoutQuery,
  fetchPlanChangePreview,
  startPlanCheckout,
  type CheckoutOutcome,
  type PlanChangeOption,
  type PlanChangePreview,
  type PlanFailureCode,
  type PlanOffer,
  type PlanTier,
} from "../../lib/api";
import { savePendingPlan, takePendingPlan } from "../../lib/draft";
import { openCheckout, type CheckoutResult } from "../../lib/checkout";
import { roomAdded } from "../../lib/room";
import { planQuery, qk } from "../../lib/queries";
import { TIER_NAMES } from "../../lib/api";
import { celebrate } from "../../lib/celebrate";
import { track } from "../../lib/telemetry";
import { PlanScreen, statedDate } from "./plan";

/** How long an acknowledgement stays on screen before it gets out of the way. */
const NOTE_MS = 2600;

/** How often, and for how long, the plan is re-read while an accepted change lands (wave 124). */
const UPDATING_POLL_MS = 1500;
const UPDATING_GIVE_UP_MS = 30_000;

/**
 * Nob's side of a plan refusal, in Nob's words — AND EVERY REFUSAL SAYS WHAT IT
 * IS (ruling R-0908-1).
 *
 * Five of these used to be one sentence. "Nob is not answering. Try that again."
 * was what a validation error, a dark feature flag, an expired session, a
 * missing subscription and a processor that refused all produced — so the one
 * refusal a person could act on and the four they could not looked identical,
 * and the owner's un-cancellable plan reported the same words as a network
 * blip. A refusal that does not say what it is teaches people to retry until
 * they give up.
 *
 * NO SENTENCE NAMES A VENDOR, A STATUS OR A CODE. "The payment side" is as
 * specific as this product gets about who refused, because the person did not
 * choose our processor and cannot act on its name.
 */
export function messageFor(code: PlanFailureCode): string {
  switch (code) {
    case "CONSENT_MISMATCH":
      // The one refusal worth explaining: the terms on screen were not the
      // terms the server would have billed on, and it refused rather than
      // guessing which of the two the buyer read.
      return "The terms on screen changed. Read them again, then choose.";
    case "NOT_SUBSCRIBED":
      return "That plan is not yours to change.";
    case "NO_SUBSCRIPTION":
      return "There is no plan on this account to change.";
    case "SAME_RUNG":
      return "You are already on that plan.";
    // The two halves of a trial's question (ruling R-0909-1). Neither is
    // something the owner did wrong, and both are fixed by asking again — so
    // both say so, and neither borrows the catch-all.
    case "NOT_TRIALING":
      return "Your trial has already ended. Reload the page and choose again.";
    case "CHOICE_REQUIRED":
      return "Say when you want this to start, then confirm.";
    case "PAYMENT_PAST_DUE":
      return "Update your card first.";
    case "VALIDATION_ERROR":
      return "That did not come through as expected. Reload the page and choose again.";
    case "PLANS_DISABLED":
      return "Plans are not open here yet. Nothing was charged.";
    case "UNAUTHENTICATED":
      return "Your session has ended. Sign in again and this will still be here.";
    case "PROCESSOR_REFUSED":
      return "The payment side would not take that. Nothing has changed — try again in a moment.";
    case "UNKNOWN":
      return "Nob is not answering. Try that again.";
  }
}

/**
 * A CHECKOUT'S REFUSAL, in the same words — with the one code whose sentence is
 * about something else (wave 103). `messageFor`'s `NO_SUBSCRIPTION` is the
 * CHANGE path's "there is no plan on this account to change", which reads as
 * nonsense to somebody trying to start one. At checkout it means the account's
 * plan record is missing, which signing in writes (`ensureSubscription`), so
 * the sentence says the one thing that repairs it.
 *
 * ONE FUNCTION FOR BOTH SCREENS THAT START A CHECKOUT — the plans view here and
 * the onboarding wizard's trial step — so a refusal is never "Nob is not
 * answering" on one of them and a sentence on the other. The catch-all is
 * `UNKNOWN` alone: a dead connection or a 5xx, where Nob really did not answer.
 */
export function checkoutMessageFor(code: PlanFailureCode): string {
  if (code === "NO_SUBSCRIPTION") {
    return "This account has no plan record to start from. Sign out and back in, then try again.";
  }
  return messageFor(code);
}

type Props = {
  /** What the browser came back from checkout with, when the address said so. */
  paid?: CheckoutOutcome | undefined;
  /**
   * Somebody pressed "Cancel" on a surface that does not own the confirmation —
   * the panel's index, beside the trial's own sentence. There is one cancel
   * path (§9 #4) and this is how another surface asks for it rather than
   * building a second.
   */
  openCancel?: boolean;
  /** Spend the intent above, so returning to this view does not re-ask. */
  onCancelOpened?: () => void;
  onSessionExpired: () => void;
  /** Opened by "Get more usage": bring the options into view (wave 57). */
  toOptions?: boolean;
  /** Opened by a conversion gate's "Start your free trial": the trial's consent, in view and focused. */
  toTrial?: boolean;
  /**
   * How often the plan is re-read while an accepted change lands. Injected so a
   * test drives the beat in milliseconds instead of racing a real 1.5 s wait
   * against a timeout; nothing in the product passes it.
   */
  pollMs?: number;
};

/**
 * The plan room's writes (docs/plans.md §14.3). The area owns the network; the
 * screen below it is a pure render, so the copy rules — a share and never a
 * capacity, one primary action on the cancel confirmation — can be asserted
 * against the screen directly.
 *
 * Every write invalidates the plan rather than reporting a new one. A tier is
 * money, and Stripe is the source of truth for it (§10.2): what this screen
 * shows after an action is what the server says, not what the client hoped.
 */
export function PlanArea({
  paid,
  openCancel,
  onCancelOpened,
  onSessionExpired,
  toOptions = false,
  toTrial = false,
  pollMs = UPDATING_POLL_MS,
}: Props) {
  const queryClient = useQueryClient();
  const { data: view, isPending } = useQuery(planQuery());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  /**
   * A CHANGE THAT HAS BEEN ACCEPTED AND NOT YET APPLIED (wave 124). `POST
   * /plan/change` answers when the processor has taken the change; the tier
   * itself moves when the processor's webhook arrives, a few seconds later. In
   * between, `/plan` still says the old tier, and a screen that showed it as
   * current after "Move to Pro 5× now" read as the change having failed.
   *
   * THE SERVER SAYS SO, not this component: `/plan` carries `pending_tier` /
   * `pending_interval` from the accepted request until the tier has moved, so a
   * reload or a second tab shows the same thing. While one is pending the plate
   * says "Updating…", no rung is current, nothing can be bought, and the plan
   * is re-read on a short beat. After `UPDATING_GIVE_UP_MS` the beat stops and
   * the plate says the change is still being applied and offers "Check again" —
   * it never quietly goes back to the old tier and re-opens the ladder.
   */
  const pending =
    view?.plan.pending_tier && view.plan.pending_interval
      ? { tier: view.plan.pending_tier, interval: view.plan.pending_interval }
      : null;
  const pendingKey = pending ? `${pending.tier}:${pending.interval}` : null;
  const [stalled, setStalled] = useState(false);
  const [checking, setChecking] = useState(false);
  const [announce, setAnnounce] = useState("");
  const wasPending = useRef<{ tier: PlanTier } | null>(null);
  useEffect(() => {
    if (!pendingKey) {
      setStalled(false);
      return;
    }
    setStalled(false);
    const started = Date.now();
    const beat = setInterval(() => {
      if (Date.now() - started > UPDATING_GIVE_UP_MS) {
        clearInterval(beat);
        setStalled(true);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: qk.plan() });
    }, pollMs);
    return () => clearInterval(beat);
  }, [pendingKey, queryClient, pollMs]);
  // THE LANDING, said once to a screen reader: the live region is always
  // mounted, so setting its text is what gets announced.
  useEffect(() => {
    if (pending) {
      wasPending.current = { tier: pending.tier };
      return;
    }
    const was = wasPending.current;
    wasPending.current = null;
    if (was && view?.plan.tier === was.tier) {
      setAnnounce(`You're on ${TIER_NAMES[was.tier]} now.`);
    }
  }, [pending, view]);
  // Where the eyes and a screen reader should be after the confirm: the status
  // line that replaced the tier, taken once when a change first becomes pending.
  const statusLine = useRef<HTMLParagraphElement>(null);
  const focusStatus = useRef(false);
  useEffect(() => {
    if (!pendingKey || !focusStatus.current) return;
    focusStatus.current = false;
    statusLine.current?.focus();
  }, [pendingKey]);
  const checkAgain = useCallback(async () => {
    setChecking(true);
    await queryClient.invalidateQueries({ queryKey: qk.plan() });
    setChecking(false);
  }, [queryClient]);

  /**
   * WHAT A NEW TIER CHANGES, AND EVERYTHING THAT READS IT (owner item 28).
   *
   * Invalidating only the plan was true and insufficient. A trial that has just
   * started is a fact about the account itself as well — the composer's meter,
   * the paywall's order, the knock the person was in the middle of writing all
   * read one of them, and a screen that still says "you cannot afford this"
   * after the money moved is the reason item 28 exists. One place, both, on
   * every success path.
   *
   * It invalidated a third key, the wallet's, until ruling R-0916-1 deleted the
   * wallet. The plan carries what that key used to: a share of the window.
   */
  const refreshMoney = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.plan() }),
      queryClient.invalidateQueries({ queryKey: qk.me() }),
    ]);
  }, [queryClient]);

  /**
   * The index asked for the confirmation. Opened once per ask, and the ask is
   * spent immediately — a flag that stayed set would reopen the question every
   * time somebody came back to this view.
   */
  const spendIntent = useRef(onCancelOpened);
  spendIntent.current = onCancelOpened;
  useEffect(() => {
    if (!openCancel) return;
    setError(null);
    setCancelling(true);
    spendIntent.current?.();
  }, [openCancel]);

  const noteTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(noteTimer.current), []);

  // Read before anything can strip it: this render is the only chance to know
  // the browser came back from checkout.
  const outcome = useRef(paid ?? checkoutOutcome());
  const announced = useRef(false);
  const expired = useRef(onSessionExpired);
  expired.current = onSessionExpired;

  const say = useCallback((text: string) => {
    setNote(text);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setNote(null), NOTE_MS);
  }, []);

  /**
   * The refusal reaches the screen in words, ALWAYS — including the expired
   * session, which used to hand off to the re-authentication flow and leave the
   * panel silent (ruling R-0908-1). The hand-off still happens; what changed is
   * that the sentence explaining why is on screen while it does, because a
   * screen that goes quiet after a tap reads as a broken button.
   */
  const fail = useCallback((code: PlanFailureCode) => {
    setError(messageFor(code));
    if (code === "UNAUTHENTICATED") expired.current();
  }, []);

  /**
   * The end of a checkout, and it is the SAME end whichever way it arrived.
   *
   * The modal reports its outcome directly (ruling R-0825-2); `?paid=` on the
   * address is the older road, kept because something can still reach the
   * processor's page at the top level — a hand-typed URL, a browser restoring a
   * tab — and a purchase that happened must never go unannounced because it
   * came home by the other door.
   *
   * The plan is named once the SERVER has been re-read: the webhook binds the
   * subscription, not this screen, so nothing here is announced on a tier the
   * client assumed. That is why `paid` sets a flag rather than announcing
   * immediately — the read may not have landed yet.
   */
  const [settled, setSettled] = useState<CheckoutResult | null>(null);

  useEffect(() => {
    const arrived = outcome.current;
    if (!arrived) return;
    outcome.current = null;
    clearCheckoutQuery();
    setSettled(arrived);
  }, []);

  useEffect(() => {
    if (!settled || announced.current) return;
    if (settled === "unpaid") {
      announced.current = true;
      setNote("Nothing was charged.");
      takePendingPlan();
      return;
    }
    if (!view) return; // named off the fresh read, or not at all
    announced.current = true;
    const chosen = takePendingPlan();
    const trialing = view.plan.state === "trialing";
    setNote(trialing ? "Your trial has started." : "Your plan has started.");
    track({ name: "plan_started", tier: chosen ?? view.plan.tier, state: view.plan.state });
    /**
     * One of the three moments this product makes something of. It is fired on
     * the RETURN, off the freshly-read plan — never on the way out to the
     * processor, where nothing has happened yet.
     */
    celebrate({
      kind: "plan",
      stamp: trialing ? "On the house" : "On",
      // The CHOSEN tier, not the cached read: the fresh plan can still be a
      // beat behind the webhook on the return, and "Free is on." over a Lite
      // purchase was the owner's screenshot (2026-08-27).
      title: `${
        TIER_NAMES[chosen !== null && chosen in TIER_NAMES ? (chosen as PlanTier) : view.plan.tier]
      } is on.`,
      body: trialing
        ? "The full allowance for the length of the trial. Nothing is charged until it ends."
        : "A fresh allowance, from today.",
    });
  }, [settled, view]);

  /**
   * WHAT THE OWNER IS BEING ASKED TO CONFIRM, or nothing (ruling R-0909-1).
   * Holding the preview here rather than in the screen keeps the screen a pure
   * render: the words on the dialog are functions of this object, and a test
   * can assert them without a network.
   */
  const [change, setChange] = useState<PlanChangePreview | null>(null);

  /**
   * Choosing a rung. A first purchase opens the checkout modal over this
   * screen (ruling R-0825-2) — nothing leaves the origin, and the panel is
   * still standing behind it when the modal closes.
   *
   * **A CHANGE ON AN EXISTING SUBSCRIPTION NO LONGER HAPPENS HERE** (ruling
   * R-0909-1). It used to: the tap posted the change and a toast said so, which
   * the owner read as nothing having happened — the toast sits at the top of a
   * room whose ladder is three screens long. Now the tap fetches what the
   * change WOULD do and opens the question; `confirmChange` below is the only
   * thing that commits, and on a trial the question has two answers and no
   * default.
   */
  const choose = useCallback(
    async (offer: PlanOffer) => {
      setBusy(true);
      setError(null);
      const onPaid = view !== null && view !== undefined && view.plan.tier !== "free";

      if (onPaid) {
        // Two questions on one screen is no question at all: opening this one
        // puts the cancel confirmation away.
        setCancelling(false);
        const r = await fetchPlanChangePreview(offer);
        setBusy(false);
        if (!r.ok) {
          fail(r.code);
          return;
        }
        setChange(r.preview);
        return;
      }

      // The withdrawal-right sentence rides with the choice when the server
      // said one applies (ToU 2.1 §8.6): it is hashed on the way out exactly as
      // it was rendered, and the server refuses a UK/EEA checkout without it.
      // The drawdown sentence is the server's and was rendered beside the
      // disclosure; without it nothing is bought (Terms 2.11 §5.3, §8.7).
      const drawdown = view?.drawdown ?? null;
      if (drawdown === null) {
        setBusy(false);
        fail("UNKNOWN");
        setError(checkoutMessageFor("UNKNOWN"));
        return;
      }
      const r = await startPlanCheckout({
        ...offer,
        drawdown,
        withdrawal_waiver: view?.withdrawal_waiver ?? null,
        surface: "billing",
      });
      setBusy(false);
      if (!r.ok) {
        fail(r.code);
        setError(checkoutMessageFor(r.code));
        // "The terms on screen changed. Read them again" is only true once the
        // screen has them: the server's current sentences — and whether the
        // §8.6 control applies — are re-read, so there is something new to read.
        if (r.code === "CONSENT_MISMATCH") {
          void queryClient.invalidateQueries({ queryKey: qk.plan() });
        }
        return;
      }
      // Remembered only so the plan can be named when the modal closes; the
      // webhook, not this, is what says it happened.
      savePendingPlan(offer.tier);
      announced.current = false;
      openCheckout({
        opening: r.opening,
        onDone: (result) => {
          // The plan is re-read FIRST and announced off what came back — and
          // "first" has to mean AWAITED: firing the refresh and settling in the
          // same tick let the announce effect read the pre-purchase plan, so a
          // trial taken through the modal celebrated as a paid plan (found by
          // the gauntlet's celebrate suite, 2026-08-27). A modal closing is not
          // a subscription; the webhook is.
          void (async () => {
            if (result === "paid") await refreshMoney();
            setSettled(result);
          })();
        },
      });
    },
    [view, fail, refreshMoney, queryClient],
  );

  /**
   * THE CONFIRM IS THE CONSENT, AND IT IS THE ONLY THING THAT SENDS (ruling
   * R-0909-1).
   *
   * The disclosure posted is the one the dialog showed — the option's own,
   * whose first-charge date belongs to the option the owner picked, not to the
   * ladder's generic offer. On a trial the two options carry two different
   * dates and therefore two different sentences and two different hashes, which
   * is what makes "I chose the one that starts at trial end" provable
   * afterwards rather than inferred.
   *
   * **NO TOAST.** The result is said where the action was: the status line
   * updates off the fresh read, and the rung carries "Starts on the 4th" in
   * place. A toast that scrolls away was the whole complaint.
   */
  const confirmChange = useCallback(
    async (option: PlanChangeOption) => {
      if (!change) return;
      setBusy(true);
      setError(null);
      const r = await changePlan({
        tier: change.tier,
        interval: change.interval,
        first_charge_on: option.first_charge_on,
        disclosure: option.disclosure,
        when: option.when,
      });
      setBusy(false);
      if (!r.ok) {
        fail(r.code);
        return;
      }
      setChange(null);
      // The server now reports the accepted upgrade as pending until the
      // webhook moves the tier; the status line takes focus when it appears.
      focusStatus.current = r.direction === "upgrade" && option.when !== "at_trial_end";
      await refreshMoney();
      // A conversation waiting at the wall carries on (wave 57). An upgrade on
      // a date not yet reached adds nothing, and the conversation that tries
      // again meets the wall again — a round trip, never a charge.
      if (r.direction === "upgrade") roomAdded();
      track({ name: "plan_changed", tier: change.tier, direction: r.direction });
    },
    [change, fail, refreshMoney],
  );

  /**
   * ONE POST, and the confirmation above it asked once. Nothing is deleted and
   * the answer says exactly how long access runs (§7, §9 #4).
   */
  const confirmCancel = useCallback(async () => {
    setBusy(true);
    setError(null);
    const r = await cancelPlan();
    setBusy(false);
    if (!r.ok) {
      fail(r.code);
      return;
    }
    setCancelling(false);
    await refreshMoney();
    say(`Cancelled. Your plan runs to ${statedDate(r.ends_on)}.`);
    track({ name: "plan_cancelled", tier: r.tier });
  }, [fail, say, refreshMoney]);

  return (
    <PlanScreen
      view={view ?? null}
      loading={isPending}
      busy={busy || pending !== null}
      updatingTo={pending?.tier ?? null}
      stalled={stalled}
      checking={checking}
      onCheckAgain={() => void checkAgain()}
      statusRef={statusLine}
      announce={announce}
      note={note}
      error={error}
      cancelling={cancelling}
      change={change}
      toOptions={toOptions}
      toTrial={toTrial}
      updateCard={<UpdateCardButton className="h-9 px-3" />}
      onChoose={(offer) => void choose(offer)}
      onConfirmChange={(option) => void confirmChange(option)}
      onDismissChange={() => setChange(null)}
      onAskCancel={() => {
        setError(null);
        setChange(null);
        setCancelling(true);
      }}
      onDismissCancel={() => setCancelling(false)}
      onConfirmCancel={() => void confirmCancel()}
    />
  );
}
